import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Field } from 'react-final-form';
import SelectFieldAdapter from 'components/shared/field-adapters/SelectFieldAdapter';
import requestStatus from 'common/constants/status';
import clientPaymentSlice from 'store/modules/clientPayment';

function ActivePaymentSelector({ clientId = null, disabled = false, onChange = null, currentPayment = null }) {
  const dispatch = useDispatch();
  const { t } = useTranslation(['labels', 'common']);

  const activePayments = useSelector((state) => state.clientPayment.data.activePayments) || [];
  const status = useSelector((state) => state.clientPayment.status.activePayments);

  useEffect(() => {
    if (clientId) {
      dispatch(clientPaymentSlice.actions.getActivePayments(clientId));
    }
  }, [clientId, dispatch]);

  // Transformar pagos activos a opciones para el select (solo nombre del plan)
  // Filtrar solo planes con sesiones disponibles (sessions_remaining > 0)
  // Excluir planes Active Plus (solo se visualizan en el calendario de grupo)
  const paymentOptions = activePayments
    .filter((payment) => !payment.sessions_paid || payment.sessions_remaining > 0)
    .filter((payment) => {
      const name = payment.plan_id?.name?.toLowerCase().replace(/\s+/g, '') || '';
      return !name.includes('activeplus');
    })
    .map((payment) => ({
      value: String(payment.id),
      label: payment.plan_id.name,
      payment
    }));

  // Al EDITAR una sesión antigua, su plan puede estar ya consumido o vencido, así que no aparece
  // entre los pagos activos. Sin esta opción de respaldo el selector abría vacío, la categoría se
  // quedaba deshabilitada y guardar cualquier cambio borraba el plan de la sesión. Se añade el
  // pago original solo si no está ya en la lista.
  const options =
    currentPayment && !paymentOptions.some((o) => o.value === String(currentPayment.id))
      ? [...paymentOptions, { value: String(currentPayment.id), label: currentPayment.label, payment: null }]
      : paymentOptions;

  return (
    <Field name="payment_id">
      {({ input }) => (
        <SelectFieldAdapter
          id="payment_id"
          input={input}
          label={t('activePlan', { ns: 'labels' }) || 'Plan activo'}
          placeholder={t('selectPlan', { ns: 'labels' }) || 'Planes'}
          fullWidth
          required
          renderInputProps={{
            fullWidth: true,
            required: true
          }}
          options={options}
          disabled={disabled || status === requestStatus.loading}
          onChange={(_event, paymentId) => {
            // paymentId es el ID directamente (no un objeto). Se compara por String porque las
            // opciones guardan el id como cadena y los pagos de Redux pueden traerlo numérico.
            if (onChange && paymentId) {
              const payment = activePayments.find((p) => String(p.id) === String(paymentId));
              onChange(payment);
            }
          }}
        />
      )}
    </Field>
  );
}

ActivePaymentSelector.propTypes = {
  clientId: PropTypes.number,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  // Pago con el que se creó la sesión que se está editando: `{ id, label }`. Solo se usa como
  // respaldo cuando ese pago ya no figura entre los activos.
  currentPayment: PropTypes.object
};

export default ActivePaymentSelector;
